/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Database schema for the human review system
 */

import { pgTable, serial, text, jsonb, timestamp, boolean, integer, index } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { agents } from './agents';

// Review session statuses
export const REVIEW_SESSION_STATUS = ['pending', 'in_progress', 'approved', 'rejected', 'expired', 'cancelled'] as const;
export type ReviewSessionStatus = typeof REVIEW_SESSION_STATUS[number];

// Field approval statuses
export const FIELD_APPROVAL_STATUS = ['pending', 'approved', 'rejected'] as const;
export type FieldApprovalStatus = typeof FIELD_APPROVAL_STATUS[number];

/**
 * Review Sessions - tracks human review sessions for agent results
 */
export const reviewSessions = pgTable('review_sessions', {
  id: text('id').primaryKey(), // UUID format: review-{timestamp}-{random}
  
  // Associated agent execution
  agentExecutionId: text('agent_execution_id'), // Reference to agent execution
  agentId: integer('agent_id').references(() => agents.id),
  agentName: text('agent_name'),
  
  // User and session info
  userId: integer('user_id').notNull(),
  sessionTitle: text('session_title').notNull(),
  sessionDescription: text('session_description'),
  
  // Schema and result data
  schemaId: text('schema_id').notNull(), // Reference to result schema
  schemaVersion: text('schema_version').notNull(),
  originalData: jsonb('original_data').notNull(), // Original agent result data
  currentData: jsonb('current_data').notNull(), // Current (potentially modified) data
  
  // Dispatch agent configuration
  dispatchAgentId: integer('dispatch_agent_id').references(() => agents.id),
  dispatchAgentName: text('dispatch_agent_name'),
  dispatchOnApproval: boolean('dispatch_on_approval').default(true),
  dispatchOnRejection: boolean('dispatch_on_rejection').default(false),
  
  // Review state
  status: text('status').notNull().default('pending').$type<ReviewSessionStatus>(),
  
  // Field-level approvals (JSON object mapping field keys to approval status)
  fieldApprovals: jsonb('field_approvals'), // Record<string, FieldApprovalStatus>
  
  // Global review metadata
  reviewerNotes: text('reviewer_notes'), // General notes from reviewer
  
  // Timestamps
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  startedAt: timestamp('started_at'), // When review actually began
  completedAt: timestamp('completed_at'), // When review was completed
  expiresAt: timestamp('expires_at'), // When session expires
}, (table) => ({
  userIdIdx: index('review_sessions_user_idx').on(table.userId),
  statusIdx: index('review_sessions_status_idx').on(table.status),
  agentIdIdx: index('review_sessions_agent_idx').on(table.agentId),
  createdAtIdx: index('review_sessions_created_idx').on(table.createdAt),
}));

/**
 * Review Comments - feedback and comments on review sessions
 */
export const reviewComments = pgTable('review_comments', {
  id: serial('id').primaryKey(),
  
  // Associated review session
  reviewSessionId: text('review_session_id').references(() => reviewSessions.id).notNull(),
  
  // Comment details
  fieldKey: text('field_key'), // null for global comments, field key for field-specific
  commentText: text('comment_text').notNull(),
  commentType: text('comment_type').default('general'), // 'general', 'suggestion', 'issue', 'approval'
  
  // Author info
  authorId: integer('author_id').notNull(),
  authorName: text('author_name').notNull(),
  
  // Timestamps
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  reviewSessionIdx: index('review_comments_session_idx').on(table.reviewSessionId),
  fieldKeyIdx: index('review_comments_field_idx').on(table.fieldKey),
  createdAtIdx: index('review_comments_created_idx').on(table.createdAt),
}));

/**
 * Review Schema Registry - stores result schemas for validation and UI generation
 */
export const reviewSchemas = pgTable('review_schemas', {
  id: text('id').primaryKey(), // Schema identifier
  name: text('name').notNull(),
  description: text('description'),
  version: text('version').notNull(),
  
  // Schema definition
  schemaDefinition: jsonb('schema_definition').notNull(), // AgentResultSchema object
  
  // Metadata
  isActive: boolean('is_active').default(true),
  createdBy: integer('created_by'),
  tags: text('tags').array(), // Array of tags for organization
  
  // Timestamps
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  nameIdx: index('review_schemas_name_idx').on(table.name),
  versionIdx: index('review_schemas_version_idx').on(table.version),
  activeIdx: index('review_schemas_active_idx').on(table.isActive),
  createdByIdx: index('review_schemas_created_by_idx').on(table.createdBy),
}));

/**
 * Review Audit Log - tracks all changes made during review sessions
 */
export const reviewAuditLog = pgTable('review_audit_log', {
  id: serial('id').primaryKey(),
  
  // Associated review session
  reviewSessionId: text('review_session_id').references(() => reviewSessions.id).notNull(),
  
  // Change details
  changeType: text('change_type').notNull(), // 'field_update', 'approval', 'comment', 'status_change'
  fieldKey: text('field_key'), // Field that was changed (null for global changes)
  oldValue: jsonb('old_value'), // Previous value
  newValue: jsonb('new_value'), // New value
  changeDescription: text('change_description'), // Human-readable description
  
  // Author info
  changedBy: integer('changed_by').notNull(),
  changedByName: text('changed_by_name').notNull(),
  
  // Timestamps
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  reviewSessionIdx: index('review_audit_log_session_idx').on(table.reviewSessionId),
  changeTypeIdx: index('review_audit_log_type_idx').on(table.changeType),
  fieldKeyIdx: index('review_audit_log_field_idx').on(table.fieldKey),
  createdAtIdx: index('review_audit_log_created_idx').on(table.createdAt),
}));

// Zod schemas for validation
export const insertReviewSessionSchema = createInsertSchema(reviewSessions).omit({
  createdAt: true,
  updatedAt: true,
});

export const selectReviewSessionSchema = createSelectSchema(reviewSessions);

export const insertReviewCommentSchema = createInsertSchema(reviewComments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectReviewCommentSchema = createSelectSchema(reviewComments);

export const insertReviewSchemaSchema = createInsertSchema(reviewSchemas).omit({
  createdAt: true,
  updatedAt: true,
});

export const selectReviewSchemaSchema = createSelectSchema(reviewSchemas);

export const insertReviewAuditLogSchema = createInsertSchema(reviewAuditLog).omit({
  id: true,
  createdAt: true,
});

export const selectReviewAuditLogSchema = createSelectSchema(reviewAuditLog);

// Type exports
export type ReviewSession = typeof reviewSessions.$inferSelect;
export type InsertReviewSession = z.infer<typeof insertReviewSessionSchema>;

export type ReviewComment = typeof reviewComments.$inferSelect;
export type InsertReviewComment = z.infer<typeof insertReviewCommentSchema>;

export type ReviewSchema = typeof reviewSchemas.$inferSelect;
export type InsertReviewSchema = z.infer<typeof insertReviewSchemaSchema>;

export type ReviewAuditLog = typeof reviewAuditLog.$inferSelect;
export type InsertReviewAuditLog = z.infer<typeof insertReviewAuditLogSchema>;
